import { proxy, subscribe } from 'valtio';
import { devtools } from 'valtio/utils';

import { clearDatadogUser, setDatadogUser } from '@cartwhl/tracking/datadog';
import getDownloadHistory from '@cartwhl/common/api/getDownloadHistory';
import getOrganization from '@cartwhl/common/api/getOrganization';
import getSpinTransactions from '@cartwhl/common/api/getSpinTransactions';
import getUserApiKey from '@cartwhl/common/api/getUserApiKey';
import { getUserInfo } from '@cartwhl/common/api/getUserInfo';
import { getUserManageSubscription } from '@cartwhl/common/api/getUserManageSubscription';
import updateUserInfo from '@cartwhl/common/api/updateUserInfo';
import * as authStore from '@cartwhl/common/stores/authStore';
import {
  AsyncState,
  OrganizationInfo,
  OrganizationMember,
  OrganizationPendingInvite,
  OrganizationServiceAccountSummary,
  PaginatedAsyncState,
  ProjectDownloadRecord,
  SpinTransaction,
  TeamWorkspaceInviteRole,
  UserInfo,
} from '@cartwhl/common/types';

import deleteOrganizationInvite from '../api/deleteOrganizationInvite';
import deleteOrganizationMember from '../api/deleteOrganizationMember';
import deleteServiceAccount from '../api/deleteServiceAccount';
import getOrganizationInvites from '../api/getOrganizationInvites';
import getVerifyEmail from '../api/getVerifyEmail';
import postJoinWaitlist from '../api/postJoinWaitlist';
import postServiceAccount from '../api/postServiceAccount';
import postOrganizationInviteAccept from '../api/postOrganizationInviteAccept';
import putOrganizationInviteResend from '../api/putOrganizationInviteResend';
import putOrganizationMember from '../api/putOrganizationMember';
import putUserProducts from '../api/putUserProducts';
import { getCookieSuffix } from '../utils';
import handleGenericErrors from '../utils/handleGenericErrors';
import * as noticesStore from './noticesStore';
import * as notificationStore from './notificationStore';
import * as updatedProductStore from './updatedProductStore';

type UserStoreState = {
  apiKey: AsyncState<{ apiKey: string; serviceID: string }>;
  organizationInfo?: OrganizationInfo;
  organizationMembers?: OrganizationMember[];
  organizationPendingInvites: OrganizationPendingInvite[];
  organizationServiceAccounts?: OrganizationServiceAccountSummary[];
  userInfo: UserInfo;
  isCartwheeler: boolean;
  isDataLabeler: boolean;
  spinTransactions: SpinTransaction[];
  spinTransactionsNextToken?: string;
  spinTransactionsLoading: boolean;
  downloadHistory: PaginatedAsyncState<ProjectDownloadRecord[]>;
  loading: boolean;
  theme: 'light' | 'dark' | null;
};

const oneMonthFromNow = new Date();
oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

const defaultOrganizationInfo: OrganizationInfo | undefined = undefined;
const defaultOrganizationMembers: OrganizationMember[] | undefined = undefined;
const defaultOrganizationServiceAccounts:
  | OrganizationServiceAccountSummary[]
  | undefined = undefined;

const defaultUser: UserInfo = {
  betaFeatures: [],
  createdAt: new Date().toISOString(),
  email: '',
  emailVerified: false,
  firstName: '',
  freeSpins: 0,
  hasAnnualSubscription: false,
  lastMonthlyRefreshAt: new Date().toISOString(),
  lastName: '',
  nextMonthlyRefreshEligibleAt: oneMonthFromNow.toISOString(),
  organizationID: '',
  planType: 'free',
  preferredName: '',
  premiumAccessUntil: new Date().toISOString(),
  purchasedSpins: 0,
  spinsAvailable: 0,
  stripeCustomerID: '',
  userID: '',
  wizardsStarted: [],
};

export const state = proxy<UserStoreState>({
  apiKey: { status: 'initial', data: undefined },
  organizationInfo: defaultOrganizationInfo,
  organizationMembers: defaultOrganizationMembers,
  organizationPendingInvites: [],
  organizationServiceAccounts: defaultOrganizationServiceAccounts,
  userInfo: defaultUser,
  isCartwheeler: false,
  isDataLabeler: false,
  spinTransactions: [],
  spinTransactionsNextToken: undefined,
  spinTransactionsLoading: false,
  downloadHistory: { status: 'initial', data: undefined },
  loading: false,
  theme: window.localStorage.getItem('darkMode') as 'light' | 'dark' | null,
});

// Initialize theme based on system preference if no stored preference
if (state.theme === null) {
  const systemPrefersDark = window.matchMedia(
    '(prefers-color-scheme: dark)',
  ).matches;
  state.theme = systemPrefersDark ? 'dark' : 'light';
}

if (state.theme) {
  setTheme(state.theme);
}

// Listen for system theme changes
const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
const handleSystemThemeChange = (e: MediaQueryListEvent) => {
  if (state.theme === null) {
    state.theme = e.matches ? 'dark' : 'light';
    setTheme(state.theme);
  }
};

mediaQuery.addEventListener('change', handleSystemThemeChange);

if (
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  '__REDUX_DEVTOOLS_EXTENSION__' in window
) {
  devtools(state, { name: 'User', enabled: true });
}

export async function load(displayErrors?: boolean, redirectTo?: string) {
  if (authStore.state.authStatus !== 'user') return;

  state.loading = true;
  const { data, error } = await getUserInfo();

  // Load the user's API key for the organization
  state.apiKey = { status: 'pending', data: undefined };
  if (data?.userInfo.organizationID) {
    const { data: apiRes, error: apiError } = await getUserApiKey();

    if (apiError) {
      state.apiKey = {
        status: 'error',
        error: apiError.message,
        data: undefined,
      };
      console.error(apiError);
    } else {
      state.apiKey = { status: 'ready', data: apiRes ?? undefined };
    }
  } else if (data) {
    // User loaded but has no org (e.g. free tier) — do not leave apiKey stuck in pending
    state.apiKey = { status: 'ready', data: undefined };
  }


  if (error) {
    const params: Record<string, any> = { suppressNotices: !displayErrors };
    if (redirectTo) {
      params.redirectTo = redirectTo;
    }
    await handleGenericErrors(error, params);
  } else if (data) {
    state.userInfo = data.userInfo;
    state.organizationInfo = data.organizationInfo;
    if (data.userInfo.organizationID) {
      // Populate members + service accounts before loading=false so profile tabs
      // never paint with userInfo/org while serviceAccounts are still undefined
      // (useEffect runs after paint; a second load() could also clear accounts).
      await fetchOrganization(data.userInfo.organizationID);
    } else {
      state.organizationMembers = undefined;
      state.organizationPendingInvites = [];
      state.organizationServiceAccounts = undefined;
    }
    setDatadogUser({
      userCreatedAt: data.userInfo.createdAt,
      id: data.userInfo.userID,
      isAnonymous: false,
      email: data.userInfo.email,
      name: data.userInfo.preferredName,
      planType: data.userInfo.planType,
    });
    // User logged in, remove anonymous user ID from localStorage if it existed
    localStorage.removeItem('anonymousUserID');
    await notificationStore.loadForLoggedInUser();
  }
  state.loading = false;
}

export async function refetchUserInfo(options?: {
  assignOnlyIfChanged?: boolean;
}) {
  const { data, error } = await getUserInfo();
  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    if (options?.assignOnlyIfChanged) {
      const sameUser =
        JSON.stringify(state.userInfo) === JSON.stringify(data.userInfo);
      const sameOrg =
        JSON.stringify(state.organizationInfo) ===
        JSON.stringify(data.organizationInfo);
      if (sameUser && sameOrg) {
        return;
      }
    }
    state.userInfo = data.userInfo;
    if (data.userInfo.organizationID) {
      // /users/info returns a slimmer organizationInfo than GET /organization (no
      // accessLevel, stripeCustomerID, etc.). Merge so profile polling does not
      // clobber fields only the org endpoint provides.
      state.organizationInfo = {
        ...(state.organizationInfo ?? {}),
        ...(data.organizationInfo ?? {}),
      } as OrganizationInfo;
    } else {
      state.organizationInfo = data.organizationInfo;
      state.organizationMembers = undefined;
      state.organizationPendingInvites = [];
      state.organizationServiceAccounts = undefined;
    }
  }
}

export async function fetchApiKey() {
  if (!state.userInfo.organizationID) return;

  state.apiKey = { status: 'pending', data: undefined };
  const { data, error } = await getUserApiKey();

  if (error) {
    state.apiKey = {
      status: 'error',
      error: error.message,
      data: undefined,
    };
  } else {
    state.apiKey = { status: 'ready', data: data ?? undefined };
  }
}

export async function clear() {
  state.userInfo = defaultUser;
  notificationStore.clear();
  state.organizationServiceAccounts = undefined;
  clearDatadogUser();
  const suffix = getCookieSuffix();

  document.cookie = `cartwheelUserID${suffix}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Domain=.getcartwheel.com;`;
  document.cookie = `cartwheelDomain${suffix}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Domain=.getcartwheel.com;`;
  document.cookie = `sessionToken${suffix}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Domain=.getcartwheel.com;`;
}

export function hasPremiumFeatures() {
  return !!(
    state.userInfo.premiumAccessUntil &&
    state.userInfo.premiumAccessUntil > new Date().toISOString()
  );
}

export function isPro() {
  return !!(
    state.userInfo.stripeCustomerID &&
    state.userInfo.premiumAccessUntil > new Date().toISOString() &&
    state.userInfo.planType === 'pro'
  );
}

export function isUnlimited() {
  return !!(
    state.userInfo.stripeCustomerID &&
    state.userInfo.premiumAccessUntil > new Date().toISOString() &&
    state.userInfo.planType === 'unlimited'
  );
}
// @mitch: does the api support partial updates?
export async function update(userDraft: Partial<UserInfo>) {
  const { data, error } = await updateUserInfo(userDraft);

  if (error) {
    await handleGenericErrors(error, { redirectTo: '/profile' });
  } else if (data) {
    state.userInfo = data.userInfo;
  }
}

export async function manageSubscription(customerBillingID: string) {
  const { data, error } = await getUserManageSubscription(customerBillingID);

  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    window.location.href = data.location;
  }
}

export async function addProducts(
  targetUserID: string,
  spinsToAdd?: number,
  premiumAccessUntil?: string,
) {
  const { data, error } = await putUserProducts(targetUserID, {
    spinsToAdd,
    premiumAccessUntil,
  });

  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    // If the target user is the current user, update the local state
    if (targetUserID === state.userInfo.userID) {
      state.userInfo = data.userInfo;
    }
    return true;
  }
}

export async function createNewServiceAccount(serviceName?: string) {
  const { data, error } = await postServiceAccount(serviceName);
  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    const orgId = state.userInfo.organizationID;
    if (orgId) {
      await fetchOrganization(orgId);
    }
    return data;
  }
}

export async function deleteOrganizationServiceAccount(serviceID: string) {
  const organizationID = state.userInfo.organizationID;
  if (!organizationID) return false;

  const { error } = await deleteServiceAccount(organizationID, serviceID);
  if (error) {
    await handleGenericErrors(error);
    return false;
  }
  await fetchOrganization(organizationID);
  return true;
}

export async function fetchSpinTransactions({
  startDate,
  endDate,
  limit,
  nextToken,
  append = false,
}: {
  startDate?: string;
  endDate?: string;
  limit?: number;
  nextToken?: string;
  append?: boolean;
}) {
  if (state.spinTransactionsLoading) return;

  state.spinTransactionsLoading = true;

  const { data, error } = await getSpinTransactions({
    startDate,
    endDate,
    limit,
    nextToken,
  });

  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    state.spinTransactions = append
      ? [...state.spinTransactions, ...data.records]
      : data.records;
    state.spinTransactionsNextToken = data.nextToken;
  }

  state.spinTransactionsLoading = false;
}

export async function fetchDownloadHistory({
  limit,
  nextToken,
  append = false,
  sortAscending,
}: {
  limit?: number;
  nextToken?: string;
  append?: boolean;
  sortAscending?: boolean;
}) {
  if (state.downloadHistory.status === 'pending') return;
  console.log('fetchDownloadHistory');

  // Preserve existing data when appending, similar to loadProjects pattern
  const existingData = append ? state.downloadHistory.data : undefined;

  state.downloadHistory = {
    status: 'pending',
    data: existingData || {
      items: [],
      nextToken: undefined,
    },
  };

  const { data, error } = await getDownloadHistory({
    limit,
    nextToken,
    sortAscending: sortAscending ?? false,
  });

  if (error) {
    await handleGenericErrors(error);
    state.downloadHistory = {
      status: 'error',
      error: error.message,
      data: undefined,
    };
  } else if (data) {
    state.downloadHistory = {
      status: 'ready',
      data: {
        items: append
          ? [...(existingData?.items || []), ...data.records]
          : data.records,
        nextToken: data.nextToken,
      },
    };
  }
}

export async function fetchOrganization(organizationID: string) {
  const { data, error } = await getOrganization(organizationID);
  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    state.organizationInfo = data.organizationInfo;
    state.organizationMembers = data.members;
    state.organizationServiceAccounts = data.serviceAccounts ?? [];
    updatedProductStore.maybeOpenPurchaseCreditsModalFromOrgInfo(
      data.organizationInfo,
    );
    updatedProductStore.maybeOpenActivateEarlyTrialFromOrgInfo(
      data.organizationInfo,
    );
  }
}

export async function fetchOrganizationPendingInvites(organizationID: string) {
  const { data, error } = await getOrganizationInvites(organizationID);
  if (error) {
    await handleGenericErrors(error);
    return;
  }
  state.organizationPendingInvites = data?.invites ?? [];
}

export async function removeOrganizationInvite(
  organizationID: string,
  inviteID: string,
) {
  const { error } = await deleteOrganizationInvite(organizationID, inviteID);
  if (error) {
    await handleGenericErrors(error);
    return false;
  }
  await fetchOrganizationPendingInvites(organizationID);
  return true;
}

export async function resendOrganizationInvite(
  organizationID: string,
  inviteID: string,
) {
  const { error } = await putOrganizationInviteResend(organizationID, inviteID);
  if (error) {
    await handleGenericErrors(error);
    return false;
  }
  noticesStore.success('Invitation email sent again.');
  return true;
}

export async function assignSeatToUser(
  userIDOrEmailAddress: string,
  seatType: 'team' | 'pro' | 'unlimited',
  options: {
    accessLevel?: 'EDIT' | 'VIEW';
    workspaceRole?: TeamWorkspaceInviteRole;
  } = {},
) {
  const organizationID = state.userInfo.organizationID;
  if (!organizationID) return;

  const { accessLevel = 'VIEW', workspaceRole } = options;

  const requiresOrgEditForTeam =
    seatType === 'team' &&
    (workspaceRole === 'admin_only' ||
      workspaceRole === 'admin_member' ||
      (!workspaceRole && accessLevel === 'EDIT'));

  if (requiresOrgEditForTeam && state.organizationInfo?.accessLevel !== 'EDIT') {
    noticesStore.error('Only team admins can assign this role.');
    return;
  }

  const body =
    seatType === 'team' && workspaceRole
      ? { seatType, workspaceRole }
      : { seatType, accessLevel };

  const { data, error } = await putOrganizationMember(
    organizationID,
    userIDOrEmailAddress,
    body,
  );

  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    if ('inviteSent' in data && data.inviteSent) {
      noticesStore.success(`Invitation email sent to ${data.email}.`);
      await fetchOrganization(organizationID);
      await fetchOrganizationPendingInvites(organizationID);
      return true;
    }
    if ('organizationInfo' in data) {
      state.organizationInfo = data.organizationInfo;
      state.userInfo = data.userInfo;
      await fetchOrganization(organizationID);
      return true;
    }
  }
}

export async function acceptPendingOrganizationInvite(
  organizationID: string,
  inviteID: string,
) {
  const { data, error } = await postOrganizationInviteAccept(
    organizationID,
    inviteID,
  );
  if (error) {
    await handleGenericErrors(error);
    return false;
  }
  if (data) {
    state.organizationInfo = data.organizationInfo;
    state.userInfo = data.userInfo;
    await fetchOrganization(organizationID);
    return true;
  }
  return false;
}

export async function removeUserFromOrganization(userIDOrEmailAddress: string) {
  const organizationID = state.userInfo.organizationID;
  if (!organizationID) return;

  const { data, error } = await deleteOrganizationMember(
    organizationID,
    userIDOrEmailAddress,
  );

  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    state.organizationInfo = data.organizationInfo;
    await fetchOrganization(organizationID);
    return true;
  }
}

export function setTheme(theme: 'light' | 'dark' | null) {
  state.theme = theme;
  if (theme === null) {
    window.localStorage.removeItem('darkMode');
    // Set to system preference when theme is null
    const systemPrefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;
    state.theme = systemPrefersDark ? 'dark' : 'light';
  } else {
    window.localStorage.setItem('darkMode', theme);
  }

  if (state.theme === 'dark') {
    document.documentElement.classList.add('dark');
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', '#222222');
  } else {
    document.documentElement.classList.remove('dark');
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', '#ffffff');
  }
}

/**
 * Joins the "notify me" waitlist with just an email address.
 * Sends a verification email to the user.
 * Returns true if successful, false otherwise.
 */
export async function joinNotifyMeWaitlist(
  email: string,
  excitedAbout?: string,
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await postJoinWaitlist(email, excitedAbout);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Verifies a waitlist email using the verification code.
 * Called from the /verify-email page after clicking the link in the email.
 * Returns true if verification was successful.
 */
export async function verifyWaitlistEmail(
  email: string,
  code: string,
  excitedAbout?: string,
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await getVerifyEmail(email, code, excitedAbout);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

subscribe(state, (ops) => {
  ops.forEach((operation) => {
    const [op, path] = operation;
    if (op === 'set' && path.includes('userInfo')) {
      const allowedStrings = [
        '@getcartwheel.com',
        'contact@s-ziadie.com',
        '0xsven@gmail.com',
      ];
      const allowedDataLabelers = [
        'adelinehmltn@gmail.com',
        'brookiem04@gmail.com',
        'jnscott2003@gmail.com',
        'jessecaro@hotmail.com',
        'jessicakeeling@gmail.com',
        'ilikelol3264@gmail.com',
        'mitchellforsey@gmail.com',
        'dylrwilliams02@gmail.com',
        'mmmcvey33@gmail.com',
        'garr3ttwolf3@gmail.com',
        'joshuamoore437@gmail.com',
        'goddardgk@hotmail.com',
        'slgoddard@hotmail.com',
      ];
      const emailLower = state.userInfo.email.toLowerCase();

      state.isCartwheeler = allowedStrings.some((str) => {
        return state.userInfo.email.includes(str);
      });
      state.isDataLabeler = [...allowedDataLabelers, ...allowedStrings].some(
        (str) => {
          return emailLower.includes(str);
        },
      );
    }
  });
});
